import { z } from "zod";
//#region src/alerting.ts
/**
* The response bodies of the CP alerting reads, gate `cp:platform:read`:
*
* - `GET /v1/admin/alerting/rules`: producer platform-api `AlertingService.listEffectiveRules`.
* - `GET /v1/admin/alerting/history`: producer `AlertingService.listHistory`, rows of
*   `platform.alert_history` (`AlertHistoryRow`) and the poller freshness (`AlertPollerFreshness`).
* - `GET /v1/admin/alerting/silences`: producer `AlertSilencesService.list` (`AlertSilenceResponse`).
* - `GET /v1/admin/alerting/active` (plan 2026-10-21-operator-tui, gap G4): the alerts the Mimir
*   ruler reports as firing now, the `MimirActiveAlert` shape of platform-api. Stage S3 (WS-9, WS-10) will add the route; no controller serves it
*   yet, so this schema is the contract that stage builds to.
*
* site-admin-api `AlertingProxyController` forwards the rules, history and silences bodies
* unchanged, and S3 will add the active route to it. So the console, the CLI and the MCP tools read
* the same shape through either service. Plan `2026-10-21-operator-tui`,
* defaults row F1.
*/
/** The two severities a CP rule carries. */
const ALERT_SEVERITIES = ["critical", "warning"];
/** The longest silence `reason`, the bound platform-api `SILENCE_REASON_MAX` applies at write. */
const ALERT_SILENCE_REASON_MAX_LENGTH = 500;
/**
* One effective rule: the code default merged with its override row.
*
* - `id`: the override row id, `null` when the rule has no override (`isDefault: true`).
* - `dataPlaneId`: always `null` today; the configuration is control-plane wide.
* - `defaultThresholdValue`, `defaultSeverity`: the code values. With `isDefault` they equal the
*   effective values.
* - `thresholdInert`: true when the threshold does not enter the rule expression.
* - `description`: the first paragraph of the rule description, placeholders named.
* - `windowSeconds`: the range window in seconds that the rule evaluates, an integer. It comes from
*   the row that supplies the effective values, else the global row, else the code window. `null`
*   for a rule with no single range window.
* - `forSeconds`: the stored `for:` in seconds, an integer, from the same rows. `null` when the
*   code `for:` applies; `defaultFor` holds that text.
* - `defaultFor`: the code `for:` text of the rule, for example `5m`.
*
* The last three keys are optional, so a client of this schema still parses the answer of a
* platform-api that does not send them yet. platform-api sends all three on every rule.
*/
const alertRuleSchema = z.object({
	id: z.uuid().nullable(),
	ruleName: z.string().min(1),
	thresholdValue: z.number(),
	severity: z.enum(ALERT_SEVERITIES),
	enabled: z.boolean(),
	dataPlaneId: z.uuid().nullable(),
	isDefault: z.boolean(),
	defaultThresholdValue: z.number(),
	defaultSeverity: z.enum(ALERT_SEVERITIES),
	thresholdInert: z.boolean(),
	description: z.string(),
	windowSeconds: z.number().int().nullable().optional(),
	forSeconds: z.number().int().nullable().optional(),
	defaultFor: z.string().optional()
}).strict();
/** `GET /v1/admin/alerting/rules`: every rule that exists in code, in the producer's order. */
const alertRulesResponseSchema = z.array(alertRuleSchema);
/**
* One row of the alert history: one firing of one rule for one label set.
*
* `severity` is text because rows written before the two-value rule stay. `threshold`,
* `dataPlaneId`, `serviceName`, `resolvedAt`, `notificationStatus` and `silencedBy` are `null`
* when absent. `value` is `null` when the stored value is NaN or an infinity: the poller stores
* those Prometheus values as they come, and JSON writes each of them as `null`. `labels` is the
* alert's label set.
*/
const alertHistoryRowSchema = z.object({
	id: z.uuid(),
	ruleName: z.string().min(1),
	severity: z.string(),
	dataPlaneId: z.uuid().nullable(),
	serviceName: z.string().nullable(),
	value: z.number().nullable(),
	threshold: z.number().nullable(),
	state: z.enum(["firing", "resolved"]),
	startedAt: z.iso.datetime(),
	resolvedAt: z.iso.datetime().nullable(),
	notificationStatus: z.string().nullable(),
	createdAt: z.iso.datetime(),
	fingerprint: z.string(),
	labels: z.record(z.string(), z.string()),
	lastSeenAt: z.iso.datetime(),
	silencedBy: z.uuid().nullable()
}).strict();
/**
* The freshness of the ruler poll. `lastSuccessfulPollAt` and `lastPollAlertCount` are `null` until
* the first successful poll. `stale` is the server's verdict and is `false` when the poller never
* ran, so a reader tests `lastSuccessfulPollAt === null` first. `pollIntervalSeconds` is display
* text only.
*/
const alertPollerFreshnessSchema = z.object({
	lastSuccessfulPollAt: z.iso.datetime().nullable(),
	lastPollAlertCount: z.number().int().nonnegative().nullable(),
	stale: z.boolean(),
	pollIntervalSeconds: z.number().positive()
}).strict();
/** `GET /v1/admin/alerting/history`: one page, newest first, the total and the poller state. */
const alertHistoryResponseSchema = z.object({
	items: z.array(alertHistoryRowSchema),
	total: z.number().int().nonnegative(),
	poller: alertPollerFreshnessSchema
}).strict();
/**
* One silence. `ruleName` and `dataPlaneId` are `null` when the silence does not narrow on them.
* `createdBy` and `expiredBy` are operator ids. `active` is the server's verdict at read time.
* `reason` is operator text of at most {@link ALERT_SILENCE_REASON_MAX_LENGTH} characters: a reader
* shows it as text.
*/
const alertSilenceSchema = z.object({
	id: z.uuid(),
	ruleName: z.string().min(1).nullable(),
	dataPlaneId: z.uuid().nullable(),
	labelMatchers: z.record(z.string(), z.string()),
	startsAt: z.iso.datetime(),
	endsAt: z.iso.datetime(),
	reason: z.string().max(500),
	createdBy: z.uuid(),
	createdAt: z.iso.datetime(),
	expiredBy: z.uuid().nullable(),
	expiredAt: z.iso.datetime().nullable(),
	active: z.boolean()
}).strict();
/** `GET /v1/admin/alerting/silences`: active silences, and expired ones on `includeExpired=true`. */
const alertSilencesResponseSchema = z.array(alertSilenceSchema);
/**
* One alert that fires now, as the Mimir ruler reports it.
*
* - `labels`: the full label set; CP rules always carry `alertname` and `severity`.
* - `annotations`: the rule annotations; a reader assumes no key.
* - `state`: always `firing`. The S3 route will drop `pending` and `inactive` alerts.
* - `activeAt`: RFC 3339, stable for one firing.
* - `value`: the expression value as Prometheus text, never a number.
*/
const activeAlertSchema = z.object({
	labels: z.record(z.string(), z.string()),
	annotations: z.record(z.string(), z.string()),
	state: z.literal("firing"),
	activeAt: z.iso.datetime({ offset: true }),
	value: z.string()
}).strict();
/**
* `GET /v1/admin/alerting/active`.
*
* `tenant` names the Mimir tenant the ruler read (`X-Scope-OrgID`), so the reader knows the scope.
* `alerts` is `[]` only when the ruler answered and nothing fires; the S3 route will answer a failed
* ruler read with a 502, never an empty list. `fetchedAt` is the CP's ISO-8601 read time.
*/
const activeAlertsResponseSchema = z.object({
	tenant: z.string().min(1),
	alerts: z.array(activeAlertSchema),
	fetchedAt: z.iso.datetime()
}).strict();
//#endregion
export { ALERT_SEVERITIES, ALERT_SILENCE_REASON_MAX_LENGTH, activeAlertSchema, activeAlertsResponseSchema, alertHistoryResponseSchema, alertHistoryRowSchema, alertPollerFreshnessSchema, alertRuleSchema, alertRulesResponseSchema, alertSilenceSchema, alertSilencesResponseSchema };

//# sourceMappingURL=alerting.js.map